import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import {
  DynamicTableColDef,
  FlyoutMenu,
  FrkBreakpoint,
  ResponsiveService,
  StickyFooterActionParam,
} from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  ColumnDefs,
  TaxInfoDownloadType,
  TaxInfoPrintType,
  TaxInfoStickySelectEvent,
  TaxListInfoAction,
  TaxListInfoFilterType,
  TaxListInfoListing,
  TaxListInfoRecordDetails,
  TaxListInfoRecordsCount,
  TaxListInfoSelectedFilters,
  TaxListInfoState,
} from '@products/types/tax-list-info.type';
import { TranslateService } from '@shared/translate/translate.service';
import { ProductDetailConfiguration, SwitchState } from '@types';
import { Logger } from '@utils/logger';
import cloneDeep from 'lodash/cloneDeep';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TaxListInfoService } from '../services/tax-list-info.service';
import { TaxDisplayComponent } from '../tax-component.interface';
import { TaxConfig } from '../tax.config';

const logger = Logger.getLogger('TaxListInfoComponent');

@Component({
  selector: 'ft-tax-list-info',
  templateUrl: './tax-list-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxListInfoComponent
  extends BaseProductDetailComponent
  implements TaxDisplayComponent, OnInit, OnDestroy {
  componentConfiguration: TaxConfig;
  gridListing: TaxListInfoListing[] = [];
  private unsubscribe$: Subject<void> = new Subject<void>();
  public isHandheld$: Observable<boolean>;
  public isPopulated = false;
  filters: TaxListInfoFilterType[];
  recordsCount: TaxListInfoRecordsCount;
  selectedFilters: TaxListInfoSelectedFilters;
  recordDetails: TaxListInfoRecordDetails;
  currentPage$: Observable<TaxListInfoListing[]>;
  currentPageNumber$: Observable<number>;
  favoritesOnlySwitchState: SwitchState; // To set default favorites only state - off/on.
  showFavoritesOnlySwitch: boolean;
  colDefs: DynamicTableColDef<TaxListInfoListing>[];
  FrkBreakpoint = FrkBreakpoint;
  rowCount = 0;
  rowCountValue = '';
  printStickyButton: StickyFooterActionParam;
  downloadStickyButton: StickyFooterActionParam;
  downloadType = TaxInfoDownloadType;
  downloadTypesMenu: Array<FlyoutMenu> = [];
  proximalTopColumnNumbers: ColumnDefs = {} as ColumnDefs;
  public isCardView$: Observable<boolean>;
  printableRecords: TaxListInfoListing[];
  isPrintEnable = false;
  printType = TaxInfoPrintType;
  public expandAll = false;
  public taxYear: string;

  public proximalPrintTop: string;
  public proximalPrintBottom: string;

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private taxListInfoService: TaxListInfoService,
    private changeDetector: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private translateService: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isCardView$ = this.responsiveService.isBreakpointOrBelow$(
      FrkBreakpoint.Tablet
    );
  }

  populate(
    componentConfig: TaxConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);

    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();

    if (this.brConfig) {
      this.colDefs = [
        {
          field: 'fundName',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-fundname`
          ),
          isCardTitle: true,
          linkField: 'link',
          sortable: true,
        },
        {
          field: 'directUSGovernmentObligations',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-directusgovernmentobligations`
          ),
          dataType: 'percent',
          sortable: true,
        },
        {
          field: 'dividendsReceivedDeduction',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-dividendsreceiveddeduction`
          ),
          dataType: 'percent',
          sortable: true,
        },
        {
          field: 'foreignSourceIncome',
          headerName: this.translateService.instant(
            `products.us-tax-info-page-foreignsourceincome`
          ),
          dataType: 'percent',
          sortable: true,
        },
        { field: '', headerName: '' },
      ];

      this.taxListInfoService
        .getTaxListInfoState$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (state: TaxListInfoState) => {
            logger.debug('Tax List Info state: ', state);
            this.isPopulated = state.isLoaded;
            if (this.isPopulated) {
              if (state.action === TaxListInfoAction.INITIAL_LOAD) {
                this.filters = cloneDeep(state.filters);
              }

              this.recordDetails = cloneDeep(state.recordDetails);
              this.taxYear = state.taxYear;
            }
            this.changeDetector.detectChanges();
          },
          (error) => {
            logger.debug('Error in fetching state: ', error);
            this.isPopulated = false;

            this.changeDetector.detectChanges();
          }
        );

      this.downloadTypesMenu = [
        {
          link: this.translateService.instant(
            `products.us-tax-info-excel-download-all`
          ),
        },
        {
          link: this.translateService.instant(
            `products.us-tax-info-excel-download-filtered`
          ),
        },
      ];

      // Get favorite only toggle details, when available.
      this.taxListInfoService
        .getFavoritesToggleState$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((favoritesState) => {
          this.showFavoritesOnlySwitch = favoritesState.showFavoritesOnlySwitch;
          this.favoritesOnlySwitchState =
            favoritesState.favoritesOnlySwitchState;
          if (this.showFavoritesOnlySwitch) {
            const favouriteLabel = this.translateService.instant(
              `products.us-tax-info-excel-download-favorite`
            );
            const isDuplicateItem = this.downloadTypesMenu.some(
              (item: FlyoutMenu) => item.link === favouriteLabel
            );
            if (!isDuplicateItem) {
              this.downloadTypesMenu.push({
                link: favouriteLabel,
              });
            }
          }
          this.proximalTopColumnNumbers = this.getProximalCaveatsColumnNumbers();
          this.changeDetector.detectChanges();
        });
      this.taxListInfoService
        .getTotalSelectedRows$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((count: number) => {
          this.rowCount = count;
          this.rowCountValue = `${
            this.rowCount
          } ${this.translateService.instant(
            'products.us-tax-info-page-sticky-item'
          )}`;
          this.changeDetector.detectChanges();
        });

      this.windowRef.onafterprint = (e: Event) => {
        this.printableRecords = [];
        this.isPrintEnable = false;
        this.changeDetector.detectChanges();
      };
      // Get current page records as observable.
      this.currentPage$ = this.taxListInfoService.getCurrentPage$();

      this.taxListInfoService.handleFavoriteToggle();

      this.taxListInfoService.populate();
    }
  }

  /**
   * Assign correct column numbers for proximal top as per 'favoritesOnlySwitch' visibility.
   */
  getProximalCaveatsColumnNumbers(): ColumnDefs {
    return this.showFavoritesOnlySwitch
      ? { s: '4', m: '8', l: '7' }
      : { s: '4', m: '8', l: '10' };
  }

  /**
   * Trigger grid filter as per the filter selection(s).
   * @param selectedFilters selected dropdown or searched text
   */
  onFilterApplied(selectedFilters: TaxListInfoSelectedFilters): void {
    // Update the class variable for future 'favoriteOnly' toggle operation.
    this.selectedFilters = selectedFilters;

    // And, send the updated details for further grid filter processes.
    this.taxListInfoService.filterListing(
      selectedFilters,
      this.favoritesOnlySwitchState === SwitchState.ON
    );

    this.expandAll = this.selectedFilters?.selectedDropdowns?.some(
      (value) => value.filterName === 'taxDataPoint'
    );
  }

  /**
   * Trigger favorites only listing as per toggle.
   * @param updatedSwitchState new switch state upon toggle action
   */
  onFavoritesOnlyToggle(updatedSwitchState: SwitchState): void {
    // Update the class variable for future 'filter' operation.
    this.favoritesOnlySwitchState = updatedSwitchState;

    // And, send the updated details for further grid filter processes.
    this.taxListInfoService.filterListing(
      this.selectedFilters,
      updatedSwitchState === SwitchState.ON
    );
  }

  /**
   * Triggers download for selected option
   * @param downloadType selected option from flyoput menu
   */
  onDownloadAction(downloadType?: TaxInfoDownloadType): void {
    this.taxListInfoService.handleDownloadAction(downloadType);
  }

  /**
   * Handle event on select and un select checkbox
   * @param selectEvent triggered event
   */
  onChangeSelection(selectEvent?: TaxInfoStickySelectEvent): void {
    this.taxListInfoService.updateSelection(selectEvent);

    this.printStickyButton = {
      hasAction: true,
      buttonLabel: this.translateService.instant(
        'products.us-tax-info-page-sticky-print'
      ),
      iconType: 'print',
    };
    this.downloadStickyButton = {
      hasAction: true,
      buttonLabel: this.translateService.instant(
        'products.us-tax-info-page-sticky-download'
      ),
      iconType: 'download',
    };
  }
  /**
   * Print functionality to be implemented TODO
   */
  print(printType?: TaxInfoPrintType, taxListInfo?: TaxListInfoListing): void {
    if (printType === TaxInfoPrintType.SELECTED) {
      this.printableRecords = this.taxListInfoService.getSelectedTaxInfo();
    } else {
      this.printableRecords = [taxListInfo];
    }
    this.isPrintEnable = true;
    this.changeDetector.detectChanges();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
